/**
 * Auth shell that switches between 4 broker-pickable variants:
 *   - split           — marketing pitch left, form right (default)
 *   - centered        — single centered card on a brand-gradient backdrop
 *   - fullscreen-hero — full-bleed hero, form floats over it
 *   - minimal         — plain centered form, no marketing
 *
 * The variant comes from `tenant.settings.authLayout`, populated by
 * `/admin/settings?tab=auth-layout`. Falls back to "split" when no
 * tenant is resolved (plain `localhost:3019`).
 */
import { Outlet, Link } from "react-router-dom";
import { useCurrentTenant } from "@/hooks/useTenant";
import type { AuthLayoutVariant } from "@/types/tenantSettings";
import { CandlestickChart, Network, ShieldCheck, Zap, type LucideIcon } from "lucide-react";
import { Brand } from "./Brand";

export function AuthLayout() {
  const { data: tenantInfo } = useCurrentTenant();
  const variant: AuthLayoutVariant =
    tenantInfo?.tenant?.settings?.authLayout ?? "split";
  const tagline = tenantInfo?.tenant?.settings?.branding?.tagline;
  const heroUrl = tenantInfo?.tenant?.settings?.branding?.authBackgroundUrl;

  if (variant === "centered") return <CenteredLayout tagline={tagline} heroUrl={heroUrl} />;
  if (variant === "minimal") return <MinimalLayout />;
  if (variant === "fullscreen-hero") return <FullscreenHeroLayout heroUrl={heroUrl} tagline={tagline} />;
  return <SplitLayout tagline={tagline} heroUrl={heroUrl} />;
}

/* ─── Split (default — marketing left, form right) ─────────────────── */
const FEATURES: { icon: LucideIcon; title: string; body: string }[] = [
  { icon: CandlestickChart, title: "MT5 live & demo accounts", body: "Open and fund in minutes" },
  { icon: Zap, title: "Fast deposits & withdrawals", body: "Bank and local transfer, tracked end to end" },
  { icon: Network, title: "Multi-level IB programme", body: "Live commissions down every tier" },
  { icon: ShieldCheck, title: "Segregated & verified", body: "KYC, 2FA and full audit trail" },
];

/** Decorative price path — shape only, no figures. */
function MarketLine() {
  return (
    <svg viewBox="0 0 600 160" className="w-full h-40" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="ml-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.28" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ml-stroke" x1="0" x2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.1" />
          <stop offset="60%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--accent))" />
        </linearGradient>
      </defs>
      <path d="M0 128 C40 120 60 132 95 112 S150 70 190 86 S250 120 290 92 S350 40 395 58 S455 96 500 54 S560 20 600 26 L600 160 L0 160Z" fill="url(#ml-fill)" />
      <path d="M0 128 C40 120 60 132 95 112 S150 70 190 86 S250 120 290 92 S350 40 395 58 S455 96 500 54 S560 20 600 26" fill="none" stroke="url(#ml-stroke)" strokeWidth="2.25" strokeLinecap="round" />
      <circle cx="600" cy="26" r="4" fill="hsl(var(--accent))" />
      <circle cx="600" cy="26" r="10" fill="hsl(var(--accent))" opacity="0.18" />
    </svg>
  );
}

function SplitLayout({ tagline, heroUrl }: { tagline?: string; heroUrl?: string }) {
  return (
    <div className="relative min-h-screen grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] overflow-hidden">
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden border-r border-border/70 p-12 xl:p-14">
        <div className="absolute inset-0 -z-10 bg-bg-subtle/60" aria-hidden />
        <div className="absolute inset-0 -z-10 bg-dots [mask-image:radial-gradient(ellipse_at_30%_20%,black,transparent_70%)]" aria-hidden />
        <div className="absolute -top-40 -left-40 -z-10 h-[34rem] w-[34rem] rounded-full bg-primary/20 blur-[120px]" aria-hidden />
        {heroUrl && (
          <div
            className="absolute inset-0 -z-10 opacity-40"
            aria-hidden
            style={{
              backgroundImage: `linear-gradient(160deg, hsl(var(--bg) / 0.55), hsl(var(--bg) / 0.95)), url(${heroUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}

        <Brand />

        <div className="max-w-lg">
          <p className="eyebrow !text-primary mb-5 flex items-center gap-2">
            <span className="h-px w-8 bg-primary/60" /> Client & partner portal
          </p>
          <h1 className="text-[44px] xl:text-5xl font-semibold leading-[1.05] tracking-[-0.03em] text-fg">
            {tagline ? (
              <span className="text-gradient">{tagline}</span>
            ) : (
              <>
                Trade global markets,
                <br />
                <span className="text-gradient">the premium way.</span>
              </>
            )}
          </h1>

          <ul className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6">
            {FEATURES.map(({ icon: Icon, title, body }, i) => (
              <li key={title} className="flex gap-3 animate-slide-up" style={{ animationDelay: `${120 + i * 70}ms` }}>
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl ring-gold text-primary">
                  <Icon className="h-[18px] w-[18px]" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-fg">{title}</span>
                  <span className="block text-[13px] leading-snug text-fg-subtle mt-0.5">{body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="-mx-12 xl:-mx-14 mb-6 opacity-90"><MarketLine /></div>
          <p className="text-xs text-fg-subtle">
            © {new Date().getFullYear()} AXFX · <Link to="/" className="hover:text-fg transition-colors">axfx.axfintech.io</Link>
          </p>
        </div>
      </aside>

      <main className="relative flex flex-col p-6 sm:p-10">
        <div className="lg:hidden mb-10"><Brand /></div>
        <div className="flex-1 grid place-items-center">
          <div className="w-full max-w-[400px] animate-slide-up">
            <Outlet />
          </div>
        </div>
        <p className="lg:hidden mt-10 text-center text-xs text-fg-subtle">© {new Date().getFullYear()} AXFX</p>
      </main>
    </div>
  );
}

/* ─── Centered — single card on a brand backdrop ───────────────────── */
function CenteredLayout({ tagline, heroUrl }: { tagline?: string; heroUrl?: string }) {
  return (
    <div
      className="relative min-h-screen grid place-items-center bg-bg overflow-hidden p-4"
      style={heroUrl ? {
        backgroundImage: `linear-gradient(135deg, hsl(var(--bg) / 0.85), hsl(var(--bg) / 0.65)), url(${heroUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      } : undefined}
    >
      {!heroUrl && (
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
          <div className="absolute inset-0 bg-grad-primary opacity-[0.08]" />
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-160 w-160 rounded-full blur-3xl opacity-25 bg-grad-primary" />
          <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 h-144 w-xl rounded-full blur-3xl opacity-20 bg-grad-accent" />
        </div>
      )}
      <div className="w-full max-w-md">
        <div className="rounded-3xl glass p-8 sm:p-10 shadow-pop">
          <div className="flex flex-col items-center text-center mb-6">
            <Brand size="lg" />
            {tagline && (
              <p className="text-sm text-fg-muted mt-3 max-w-xs">{tagline}</p>
            )}
          </div>
          <Outlet />
        </div>
        <p className="text-center text-xs text-fg-subtle mt-4">
          © {new Date().getFullYear()} AXFX · <Link to="/" className="hover:text-fg">axfx.axfintech.io</Link>
        </p>
      </div>
    </div>
  );
}

/* ─── Fullscreen hero — bg image, card floats over it ──────────────── */
function FullscreenHeroLayout({ heroUrl, tagline }: { heroUrl?: string; tagline?: string }) {
  const bg: React.CSSProperties = heroUrl
    ? {
        backgroundImage: `linear-gradient(135deg, hsl(var(--bg) / 0.85), hsl(var(--bg) / 0.65)), url(${heroUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : {};

  return (
    <div className="relative min-h-screen grid place-items-center bg-bg overflow-hidden p-4" style={bg}>
      {!heroUrl && (
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
          {/* Stronger gradients when no auth-bg uploaded — page shouldn't
              feel empty just because the broker hasn't uploaded a hero
              image yet. The tenant's primaryColor drives the gradient. */}
          <div className="absolute inset-0 bg-grad-primary opacity-40" />
          <div className="absolute -top-40 left-1/4 h-160 w-160 rounded-full blur-3xl opacity-50 bg-grad-primary" />
          <div className="absolute -bottom-40 right-1/4 h-160 w-160 rounded-full blur-3xl opacity-40 bg-grad-accent" />
          <div className="absolute inset-0 bg-bg/60" />
        </div>
      )}
      <div className="w-full max-w-md">
        <div className="rounded-3xl bg-bg-elevated/95 backdrop-blur-xl border border-border p-8 sm:p-10 shadow-pop">
          <div className="flex flex-col items-center text-center mb-6">
            <Brand size="lg" />
            {tagline && (
              <p className="text-sm text-fg-muted mt-3 max-w-xs">{tagline}</p>
            )}
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/* ─── Minimal — bare centered form, no marketing ───────────────────── */
function MinimalLayout() {
  return (
    <div className="min-h-screen grid place-items-center bg-bg p-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <Brand size="lg" />
        </div>
        <Outlet />
      </div>
    </div>
  );
}
